import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional override for the fallback UI. */
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global error boundary. Wraps the entire app in main.tsx so a crash in any
 * route renders a friendly fallback with a Retry button instead of a white
 * screen. We intentionally keep this dependency-free and stateless beyond the
 * caught error.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Future hook: send to an error reporter. For now log to console.
    console.error('[ErrorBoundary] uncaught error', error, info.componentStack);
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback) return this.props.fallback;

    const message = this.state.error?.message ?? 'Something went wrong.';

    return (
      <div className="flex min-h-screen items-center justify-center bg-white px-6 dark:bg-[#0B1220]">
        <div className="w-full max-w-sm text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
            </svg>
          </div>
          <h1 className="text-base font-semibold text-ink-900 dark:text-[#E2E8F0]">Something went wrong</h1>
          <p className="mt-1 text-sm text-ink-500 dark:text-[#94A3B8]">{message}</p>
          <div className="mt-6 flex gap-2">
            <button
              type="button"
              onClick={this.handleRetry}
              className="flex-1 rounded-md border border-ink-200 bg-white px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50 dark:border-[#334155] dark:bg-transparent dark:text-[#94A3B8] dark:hover:bg-[#0F172A]"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="flex-1 rounded-md bg-[#2563EB] px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Reload app
            </button>
          </div>
        </div>
      </div>
    );
  }
}
